'use client';

import { useState } from 'react';
import Link from 'next/link';
import { BENEFICIAL_INSECTS, CATEGORY_LABELS } from '@/data/beneficialInsects';
import type { BeneficialCategory } from '@/data/beneficialInsects';
import BeneficialImage from '@/components/BeneficialImage';

// ── Filter config ──────────────────────────────────────────────────────────────

const FILTERS: { value: BeneficialCategory | 'all'; label: string }[] = [
  { value: 'all',              label: 'All'              },
  { value: 'pollinator',       label: 'Pollinators'      },
  { value: 'predator',         label: 'Predators'        },
  { value: 'parasitoid',       label: 'Parasitoids'      },
  { value: 'soil-ally',        label: 'Soil Allies'      },
  { value: 'vertebrate-ally',  label: 'Vertebrate Allies'},
];

const CATEGORY_BADGE: Record<BeneficialCategory, string> = {
  pollinator:        'bg-[#B85C8A] text-white',
  predator:          'bg-[#C2703E] text-white',
  parasitoid:        'bg-[#7C5CBF] text-white',
  'soil-ally':       'bg-[#8B6F47] text-white',
  'vertebrate-ally': 'bg-[#2D5016] text-white',
};

// ── Icons ──────────────────────────────────────────────────────────────────────

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round">
      <circle cx="6.5" cy="6.5" r="4.5" />
      <path d="M10.5 10.5L14 14" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round">
      <path d="M1.5 1.5L10.5 10.5M10.5 1.5L1.5 10.5" />
    </svg>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function BeneficialContent() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<BeneficialCategory | 'all'>('all');

  const q = search.trim().toLowerCase();

  const filtered = BENEFICIAL_INSECTS.filter(b => {
    if (category !== 'all' && b.category !== category) return false;
    if (!q) return true;
    return (
      b.name.toLowerCase().includes(q) ||
      b.shortDescription.toLowerCase().includes(q) ||
      b.category.toLowerCase().includes(q) ||
      CATEGORY_LABELS[b.category].toLowerCase().includes(q) ||
      b.helpsControl.some(h => h.toLowerCase().includes(q)) ||
      b.attractWith.some(a => a.toLowerCase().includes(q))
    );
  }).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-5">

      {/* Back */}
      <div>
        <Link href="/learn"
          className="font-body text-[13px] text-sage hover:text-deep-green transition-colors">
          ← Learn
        </Link>
      </div>

      {/* Header */}
      <div>
        <h1 className="font-heading text-[28px] text-dark-text leading-tight">
          Garden Allies
        </h1>
        <p className="font-body text-[16px] text-sage mt-1">
          Pollinators, predators, and natural pest controllers
        </p>
      </div>

      {/* Intro callout */}
      <div className="bg-light-sage rounded-xl border border-sage/30 p-4">
        <p className="font-body text-[14px] font-semibold text-deep-green">
          Encourage, don&rsquo;t just control
        </p>
        <p className="font-body text-[13px] text-sage mt-0.5 leading-relaxed">
          A garden with a strong population of beneficial insects rarely needs pesticide intervention.
          Plant diversity, undisturbed habitat, and no broad-spectrum sprays are the three things that
          matter most.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sage pointer-events-none">
          <SearchIcon />
        </span>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder='Search — try "aphids", "pollinator", "snake"...'
          className="w-full font-body text-[15px] text-dark-text bg-white border border-sand
                     rounded-xl pl-9 pr-9 py-2.5 focus:outline-none focus:border-sage
                     placeholder:text-sand transition-colors"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-sand hover:text-sage transition-colors">
            <XIcon />
          </button>
        )}
      </div>

      {/* Category filter chips */}
      <div className="flex gap-2 flex-wrap">
        {FILTERS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setCategory(value)}
            className={`font-body text-[13px] font-medium px-4 py-1.5 rounded-full border
                        transition-all duration-150 whitespace-nowrap
                        ${category === value
                          ? 'bg-deep-green text-white border-deep-green'
                          : 'bg-white text-dark-text border-sand hover:border-sage'
                        }`}>
            {label}
          </button>
        ))}
      </div>

      {/* Results count */}
      <p className="font-body text-[13px] text-sage">
        {filtered.length === BENEFICIAL_INSECTS.length
          ? `${BENEFICIAL_INSECTS.length} garden allies`
          : `${filtered.length} of ${BENEFICIAL_INSECTS.length} results`}
        {category !== 'all' && ` in ${CATEGORY_LABELS[category]}`}
      </p>

      {/* Grid */}
      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {filtered.map(b => (
            <Link
              key={b.id}
              href={`/learn/beneficial/${b.id}`}
              className="group bg-white rounded-xl border border-sand p-5 flex flex-col gap-3
                         hover:border-sage hover:shadow-sm transition-all duration-200">

              {/* Badge + image */}
              <div className="flex items-start justify-between gap-3">
                <span className={`inline-block font-body text-[11px] font-semibold
                                  px-2.5 py-0.5 rounded-full ${CATEGORY_BADGE[b.category]}`}>
                  {CATEGORY_LABELS[b.category]}
                </span>
                <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden
                                border border-sand bg-white">
                  <BeneficialImage
                    id={b.id}
                    name={b.name}
                    width={80}
                    height={80}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              </div>

              {/* Name + description */}
              <div>
                <h2 className="font-heading text-[18px] text-dark-text leading-snug
                               group-hover:text-deep-green transition-colors">
                  {b.name}
                </h2>
                <p className="font-body text-[14px] text-sage mt-1 leading-relaxed line-clamp-2">
                  {b.shortDescription}
                </p>
              </div>

              {/* Helps control tags */}
              {b.helpsControl.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {b.helpsControl.slice(0, 3).map(pest => (
                    <span key={pest}
                      className="font-body text-[11px] text-warm-brown bg-cream
                                 px-2 py-0.5 rounded-full border border-sand">
                      {pest}
                    </span>
                  ))}
                  {b.helpsControl.length > 3 && (
                    <span className="font-body text-[11px] text-sage bg-cream
                                     px-2 py-0.5 rounded-full border border-sand">
                      +{b.helpsControl.length - 3} more
                    </span>
                  )}
                </div>
              )}

              <div className="mt-auto pt-1">
                <span className="font-body text-[13px] font-semibold text-deep-green
                                 group-hover:underline">
                  Learn more →
                </span>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-sand p-10 flex flex-col
                        items-center justify-center text-center gap-3">
          <p className="font-heading text-[18px] text-dark-text">No results found</p>
          <p className="font-body text-[14px] text-sage">Try a different search term or filter</p>
          <button
            onClick={() => { setSearch(''); setCategory('all'); }}
            className="font-body text-[13px] font-semibold text-deep-green hover:underline">
            Clear filters
          </button>
        </div>
      )}

    </div>
  );
}
